import React from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Wallet, FileSpreadsheet, FileText } from "lucide-react";

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function thisMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function DayRows({ days }) {
    return (
        <div className="mt-3 border-t border-border/60 pt-3 space-y-1 text-xs">
            {days.length === 0 && <div className="text-brand-stone italic">No attendance recorded this month.</div>}
            {days.map((d) => (
                <div key={d.date} className="flex flex-wrap gap-3 items-center">
                    <span className="w-24 font-medium">{d.date}</span>
                    <span className="w-16 capitalize">{d.status}</span>
                    {d.sections > 0 && <span>{d.sections} section{d.sections === 1 ? "" : "s"}</span>}
                    {d.minutes_late > 0 && <span className="text-amber-700">{d.minutes_late} min late (−{usd(d.deduction_cents)})</span>}
                    <span className="ml-auto font-semibold">{usd(d.net_cents)}</span>
                </div>
            ))}
        </div>
    );
}

function PayRow({ r }) {
    const [open, setOpen] = React.useState(false);
    return (
        <Card data-testid={`payroll-row-${r.teacher_id}`}>
            <CardContent className="p-4">
                <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-4 flex-wrap text-left" data-testid={`payroll-toggle-${r.teacher_id}`}>
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <div className="min-w-0 flex-1">
                        <div className="font-medium flex items-center gap-2">
                            {r.full_name}
                            <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px]">
                                {r.staff_type === "teacher_volunteer" ? "Teacher/Volunteer" : "Teacher"}
                            </Badge>
                        </div>
                        <div className="text-xs text-brand-stone">
                            {r.days_present} day{r.days_present === 1 ? "" : "s"} present
                            {r.staff_type === "teacher_volunteer" && ` · ${r.sections} sections`}
                            {` · ${r.minutes_late} min late · ${r.days_absent} absent`}
                        </div>
                    </div>
                    <div className="text-right text-xs">
                        <div className="text-brand-stone">Gross {usd(r.gross_cents)} · −{usd(r.deduction_cents)}</div>
                        <div className="text-lg font-semibold text-brand-ink" data-testid={`payroll-net-${r.teacher_id}`}>{usd(r.net_cents)}</div>
                    </div>
                </button>
                {open && <DayRows days={r.days} />}
            </CardContent>
        </Card>
    );
}

async function download(month, format) {
    try {
        const res = await api.get("/superadmin/payroll/export", { params: { month, format }, responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `staff-pay-${month}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch {
        toast.error("Download failed");
    }
}

export default function StaffPayroll() {
    const [month, setMonth] = React.useState(thisMonth());
    const [data, setData] = React.useState(null);

    React.useEffect(() => {
        if (month) {
            api.get("/superadmin/payroll", { params: { month } }).then((r) => setData(r.data));
        }
    }, [month]);

    return (
        <div data-testid="staff-payroll">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2 font-dash font-semibold"><Wallet className="w-4 h-4 text-primary" /> Monthly staff pay</div>
                    <div className="text-xs text-brand-stone mt-1">Teacher $50/day · Teacher/Volunteer $25/section · −$1 per minute late (never below $0/day) · absent = $0</div>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                    <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="payroll-month" />
                    <Button variant="outline" size="sm" className="gap-1.5" disabled={!month} onClick={() => download(month, "csv")} data-testid="payroll-export-csv">
                        <FileSpreadsheet className="w-4 h-4" /> Spreadsheet
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5" disabled={!month} onClick={() => download(month, "pdf")} data-testid="payroll-export-pdf">
                        <FileText className="w-4 h-4" /> PDF
                    </Button>
                    <div className="text-right">
                        <div className="text-[11px] text-brand-stone">Total payout</div>
                        <div className="text-xl font-semibold" data-testid="payroll-total">{usd(data?.total_net_cents)}</div>
                    </div>
                </div>
            </div>
            <div className="space-y-2">
                {data?.rows.map((r) => <PayRow key={r.teacher_id} r={r} />)}
                {data && data.rows.length === 0 && <div className="text-center py-12 text-brand-stone">No teachers yet.</div>}
            </div>
        </div>
    );
}
